import { useEffect, useState } from "react";
import axios from "axios";
import { Crown, Sparkles, UserPlus, X } from "lucide-react";
import { BACKEND_URL } from "../lib/api";
import { logger } from "../lib/logger";

// "5 minutes ago" / "2 hours ago"
function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const ICONS = {
  founder: Crown,
  signup: UserPlus,
  clone: Sparkles,
};

const COLORS = {
  founder: "bg-yellow-500 text-zinc-950",
  signup: "bg-brand text-white",
  clone: "bg-purple-500 text-white",
};

const HIDE_KEY = "cf_proof_hidden_until";

export default function SocialProofTicker() {
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);
  const [hidden, setHidden] = useState(false);
  const [meta, setMeta] = useState({ total_founders_sold: 0, founders_seats_remaining: 100 });

  // Respect snooze (user dismissed within last 24h)
  useEffect(() => {
    try {
      const until = parseInt(localStorage.getItem(HIDE_KEY) || "0", 10);
      if (until > Date.now()) setHidden(true);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (hidden) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/social-proof/recent`);
        if (cancelled) return;
        setEvents(data.events || []);
        setMeta({
          total_founders_sold: data.total_founders_sold || 0,
          founders_seats_remaining: data.founders_seats_remaining ?? 100,
        });
      } catch (e) {
        logger.error("social proof load", e);
      }
    };
    load();
    const id = setInterval(load, 60000); // refresh every minute
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [hidden]);

  useEffect(() => {
    if (events.length <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % events.length), 6000);
    return () => clearInterval(id);
  }, [events.length]);

  const dismiss = () => {
    setHidden(true);
    try {
      localStorage.setItem(HIDE_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    } catch (e) { /* ignore */ }
  };

  if (hidden) return null;
  // Don't show a ticker until we have real events. No fake names.
  if (!events.length) return null;

  const ev = events[idx % events.length];
  const Icon = ICONS[ev.kind] || Sparkles;
  const color = COLORS[ev.kind] || "bg-zinc-900 text-white";

  return (
    <div
      data-testid="social-proof-ticker"
      className="fixed bottom-4 left-4 z-40 max-w-[340px] animate-fade-in"
      style={{ animationDuration: "0.4s" }}
    >
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 shadow-lg flex items-stretch overflow-hidden">
        <div className={`flex items-center justify-center w-11 ${color}`}>
          <Icon className="w-5 h-5" strokeWidth={2.2} />
        </div>
        <div className="flex-1 px-3 py-2.5 pr-7 relative">
          <p className="text-sm font-semibold text-zinc-900 dark:text-white leading-tight">
            {ev.label}
          </p>
          <div className="flex items-center justify-between mt-1">
            <p className="text-[11px] text-zinc-500">{timeAgo(ev.at)}</p>
            {meta.total_founders_sold > 0 && ev.kind === "founder" && (
              <p className="text-[10px] font-bold text-yellow-600 dark:text-yellow-500 uppercase tracking-wider">
                {meta.founders_seats_remaining} seats left
              </p>
            )}
          </div>
          <button
            onClick={dismiss}
            data-testid="social-proof-dismiss"
            className="absolute top-1.5 right-1.5 text-zinc-300 hover:text-zinc-600"
            aria-label="Hide"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
